// AdminUploadCurrentStudents.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Chip,
  DialogActions,
  Dialog as MuiDialog,
  LinearProgress,
  Divider,
  Tabs,
  Tab,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ListIcon from '@mui/icons-material/List';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PeopleIcon from '@mui/icons-material/People';
import SendIcon from '@mui/icons-material/Send';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LinkIcon from '@mui/icons-material/Link';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import LoadingSkeleton from 'components/LoadingSkeleton';
import { adminStyles, loadingConfig } from 'styles/adminStyles';
import * as XLSX from 'xlsx';
import api from '../../../API/axiosInstance';
import { API_ENDPOINTS } from 'config/api';
import AdminUploadedCurrentStudentsDialog from './AdminUploadedCurrentStudentsDialog';

ModuleRegistry.registerModules([AllCommunityModule]);
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const AdminUploadCurrentStudents = ({ open, onClose }) => {
  const [currentTab, setCurrentTab] = useState(0);
  
  // Upload tab states
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [sheetName, setSheetName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [importedRecords, setImportedRecords] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Duplicate detection
  const [duplicates, setDuplicates] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  
  // Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  
  // Uploaded data dialog
  const [uploadedDataDialogOpen, setUploadedDataDialogOpen] = useState(false);
  const handleOpenUploadedDataDialog = () => setUploadedDataDialogOpen(true);
  const handleCloseUploadedDataDialog = () => setUploadedDataDialogOpen(false);
  
  // Uploaded Details tab states
  const gridRef = useRef(null);
  const [uploadedStudents, setUploadedStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);

  const resetAll = () => {
    setFile(null);
    setParsing(false);
    setSheetName('');
    setHeaders([]);
    setRows([]);
    setIsDownloading(false);
    setIsUploading(false);
    setConfirmOpen(false);
    setUploadStatus(null);
    setResponseMessage('');
    setImportedRecords(0);
    setErrorMessage('');
    setDuplicates([]);
    setShowPreview(false);
    setOpenSnackbar(false);
    setSnackbarMessage('');
    setSnackbarSeverity('info');
  };

  const handleCloseDialog = () => {
    resetAll();
    setCurrentTab(0);
    setUploadedStudents([]);
    setStats(null);
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (newValue === 1) {
      // Load uploaded students when switching to details tab
      fetchUploadedStudents();
      fetchStats();
    }
  };

  const checkDuplicatesInFile = (data) => {
    const duplicates = [];
    const seenRecords = new Map(); // Key: Email|Contact|WhatsApp, Value: {rowIndex, name}

    data.forEach((row, index) => {
      // Support both new format (user-friendly) and old format (backward compatibility)
      const email = (row['Email ID'] || row['Email'] || '').toString().trim().toLowerCase();
      const contact = (row['Contact Number'] || row['Mobile'] || '').toString().trim();
      const whatsapp = (row['Whatsapp Number'] || row['WhatsAppNumber'] || '').toString().trim();
      const name = (row['Name'] || row['FirstName'] || '').toString().trim();

      // Check for duplicates based on Email ID, Contact Number, or WhatsApp Number
      const keys = [];
      if (email) keys.push({ type: 'Email', value: email });
      if (contact) keys.push({ type: 'Contact', value: contact });
      if (whatsapp) keys.push({ type: 'WhatsApp', value: whatsapp });

      keys.forEach(({ type, value }) => {
        const key = `${type}:${value}`;
        if (seenRecords.has(key)) {
          const existing = seenRecords.get(key);
          duplicates.push({
            rowIndex: index + 1, // 1-based for display
            name: name || 'N/A',
            email: email || 'N/A',
            contact: contact || 'N/A',
            whatsapp: whatsapp || 'N/A',
            reason: `Duplicate ${type.toLowerCase()} found at row ${existing.rowIndex}`,
            duplicateWith: existing.rowIndex,
          });
        } else {
          seenRecords.set(key, { rowIndex: index + 1, name });
        }
      });
    });

    return duplicates;
  };

  const fetchUploadedStudents = async () => {
    setLoadingStudents(true);
    try {
      const response = await api.get(API_ENDPOINTS.STUDENT_REQUEST.GET_ALL_UPLOADED_STUDENTS(true));
      // console.log('API Response:', response.data);
      // console.log('Response length:', response.data?.length);
      if (response.data && response.data.length > 0) {
        // console.log('First item structure:', response.data[0]);
        // console.log('First item keys:', Object.keys(response.data[0]));
      }
      
      // Convert PascalCase to camelCase for grid compatibility - Simplified format
      const convertedData = (response.data || []).map((item, index) => {
        const formatDate = (dateValue) => {
          if (!dateValue) return "-";
          try {
            return new Date(dateValue).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
          } catch {
            return "-";
          }
        };

        const converted = {
        uploadId: item.uploadId || item.UploadId,
        id: item.id || item.Id || item.erpStudentId || item.ErpStudentId || "-",
        erpStudentId: item.erpStudentId || item.ErpStudentId || item.id || item.Id,
        rollNum: item.rollNum || item.RollNum || item.rollNumber || item.RollNumber || "-",
        regNum: item.regNum || item.RegNum || item.regNumber || item.RegNumber || "-",
        rollNumber: item.rollNumber || item.RollNumber || item.rollNum || item.RollNum || "-",
        regNumber: item.regNumber || item.RegNumber || item.regNum || item.RegNum || "-",
        name: item.name || item.Name || item.fullName || item.FullName || `${item.firstName || item.FirstName || ''} ${item.lastName || item.LastName || ''}`.trim() || "-",
        firstName: item.firstName || item.FirstName || "-",
        lastName: item.lastName || item.LastName || "-",
        fullName: item.fullName || item.FullName || item.name || item.Name || `${item.firstName || item.FirstName || ''} ${item.lastName || item.LastName || ''}`.trim() || "-",
        gender: item.gender || item.Gender || "-",
        dateOfBirth: formatDate(item.dateOfBirth || item.DateOfBirth),
        maritalStatus: item.maritalStatus || item.MaritalStatus || "-",
        batchYear: item.batchYear || item.BatchYear || "-",
        collegeStudied: item.collegeStudied || item.CollegeStudied || "-",
        courseStudied: item.courseStudied || item.CourseStudied || "-",
        branch: item.branch || item.Branch || "-",
        contactNumber: item.contactNumber || item.ContactNumber || "-",
        whatsappNumber: item.whatsappNumber || item.WhatsappNumber || item.whatsAppNumber || item.WhatsAppNumber || "-",
        emailID: item.emailID || item.EmailID || item.email || item.Email || "-",
        email: item.email || item.Email || item.emailID || item.EmailID || "-",
        mobile: item.mobile || item.Mobile || item.contactNumber || item.ContactNumber || "-",
        presentStatus: item.presentStatus || item.PresentStatus || "-",
        forOthers: item.forOthers || item.ForOthers || "-",
        organization: item.organization || item.Organization || "-",
        designation: item.designation || item.Designation || "-",
        location: item.location || item.Location || "-",
        availability: item.availability || item.Availability || "-",
        feedback: item.feedback || item.Feedback || "-",
        nativePlace: item.nativePlace || item.NativePlace || "-",
        domain: item.domain || item.Domain || "-",
        category: item.category || item.Category || "-",
        uploadedBy: item.uploadedBy || item.UploadedBy || "-",
        uploadedDate: formatDate(item.uploadedDate || item.UploadedDate),
        isActive: item.isActive ?? item.IsActive,
        };
        
        if (index === 0) {
          // console.log('First converted item:', converted);
          // console.log('First converted item keys:', Object.keys(converted));
        }
        
        return converted;
      });
      
      // console.log('Converted data:', convertedData);
      if (convertedData.length > 0) {
        // console.log('Sample row data for grid:', {
        //   id: convertedData[0].id,
        //   name: convertedData[0].name,
        //   rollNumber: convertedData[0].rollNumber,
        //   emailID: convertedData[0].emailID,
        // });
      }
      setUploadedStudents(convertedData);
    } catch (error) {
      console.error('Error fetching uploaded students:', error);
      setErrorMessage('Failed to load uploaded students. Please try again.');
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.STUDENT_REQUEST.GET_UPLOAD_STATS);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const statusCellRenderer = useCallback((params) => {
    const data = params.data;
    if (data.portalRequestStatus) {
      const status = data.portalRequestStatus.toLowerCase();
      const color = status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'warning';
      return (
        <Chip 
          label={data.portalRequestStatus} 
          color={color} 
          size="small" 
          sx={{ fontSize: '11px', height: '24px' }}
        />
      );
    }
    return (
      <Chip 
        label="No Request" 
        color="default" 
        size="small" 
        sx={{ fontSize: '11px', height: '24px' }}
      />
    );
  }, []);

  const invitationStatusCellRenderer = useCallback((params) => {
    const sent = params.data.invitationSent;
    return (
      <Chip 
        label={sent ? 'Sent' : 'Pending'} 
        color={sent ? 'success' : 'warning'} 
        size="small" 
        sx={{ fontSize: '11px', height: '24px' }}
      />
    );
  }, []);

  // Text cell renderer for long text with ellipsis
  const textCellRenderer = useCallback((params) => {
    const value = params.value ?? "-";
    return (
      <span 
        title={value} 
        style={{ 
          display: "inline-block", 
          maxWidth: "100%", 
          overflow: "hidden", 
          textOverflow: "ellipsis", 
          whiteSpace: "nowrap",
          fontSize: "0.75rem"
        }}
      >
        {value}
      </span>
    );
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 55,
      minWidth: 50,
      maxWidth: 60,
      pinned: "left",
      resizable: false,
      sortable: false,
      filter: false,
      lockPosition: true,
      cellStyle: { textAlign: "center", fontSize: "0.75rem" },
    },
    { 
      field: "id", 
      headerName: "Id", 
      width: 80, 
      minWidth: 70,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
    },
    { 
      field: "rollNumber", 
      headerName: "Roll Number", 
      width: 100, 
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "regNumber", 
      headerName: "Registration Number", 
      width: 130, 
      minWidth: 110,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "name", 
      headerName: "Name", 
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "gender", 
      headerName: "Gender", 
      width: 80, 
      minWidth: 70,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    { 
      field: "dateOfBirth", 
      headerName: "Date of Birth", 
      width: 100, 
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "maritalStatus", 
      headerName: "Marital Status", 
      width: 100, 
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    { 
      field: "batchYear", 
      headerName: "Batch Year", 
      width: 85, 
      minWidth: 75,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    { 
      field: "collegeStudied", 
      headerName: "College Studied", 
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "courseStudied", 
      headerName: "Course Studied", 
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "branch", 
      headerName: "Branch", 
      flex: 1,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "contactNumber", 
      headerName: "Contact Number", 
      width: 110, 
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "whatsappNumber", 
      headerName: "WhatsApp Number", 
      width: 110, 
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "emailID", 
      headerName: "Email ID", 
      flex: 1.5,
      minWidth: 130,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "presentStatus", 
      headerName: "Present Status", 
      flex: 1,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "organization", 
      headerName: "Organization", 
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "designation", 
      headerName: "Designation", 
      flex: 1,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "location", 
      headerName: "Location", 
      flex: 1,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "uploadedDate", 
      headerName: "Uploaded Date", 
      width: 110, 
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    { 
      field: "isActive", 
      headerName: "Is Active", 
      width: 80, 
      minWidth: 70,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellStyle: (params) => {
        return {
          fontSize: "0.75rem",
          ...(params.value === true || params.value === "Yes" 
            ? { color: "#4caf50", fontWeight: "bold" }
            : { color: "#f44336", fontWeight: "bold" })
        };
      },
      valueFormatter: (params) => {
        if (params.value === true || params.value === "Yes") return "Yes";
        return "No";
      },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
  ], [textCellRenderer]);

  const humanSize = (nBytes) => {
    if (!nBytes && nBytes !== 0) return '';
    const units = ['Bytes', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = nBytes;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i += 1;
    }
    return `${size.toFixed(2)} ${units[i]}`;
  };

  const handleFileChange = async (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;

    setFile(selected);
    await parseExcel(selected);
  };

  const parseExcel = async (selectedFile) => {
    setParsing(true);
    setHeaders([]);
    setRows([]);
    setErrorMessage('');
    setDuplicates([]);
    setShowPreview(false);
    try {
      const data = await selectedFile.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });

      const wsName = wb.SheetNames[0];
      const ws = wb.Sheets[wsName];

      // Convert to JSON with header row auto-detected
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });

      // Capture headers (from the first row keys)
      const hdrs = json.length ? Object.keys(json[0]) : [];

      setSheetName(wsName);
      setHeaders(hdrs);
      setRows(json);

      // Check for duplicates within the file
      const duplicateList = checkDuplicatesInFile(json);
      setDuplicates(duplicateList);
      setShowPreview(true);
    } catch (err) {
      console.error('Excel parse error:', err);
      setErrorMessage('Failed to parse the Excel file. Please ensure it is a valid .xlsx or .xls file.');
    } finally {
      setParsing(false);
    }
  };

  const downloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get(API_ENDPOINTS.STUDENT_REQUEST.DOWNLOAD_TEMPLATE, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'CurrentStudentsTemplate.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      setErrorMessage('Failed to download the template. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleAskConfirm = () => {
    if (!file || rows.length === 0) {
      setErrorMessage('Please upload a valid Excel file with at least one data row before importing.');
      return;
    }
    if (duplicates.length > 0) {
      setErrorMessage(`Found ${duplicates.length} duplicate record(s) in the file. Please remove duplicates before importing.`);
      setSnackbarMessage(`Found ${duplicates.length} duplicate record(s). Please review and remove them.`);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }
    setConfirmOpen(true);
  };

  const handleImport = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadStatus(null);
    setErrorMessage('');
    setConfirmOpen(false);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(
        API_ENDPOINTS.STUDENT_REQUEST.UPLOAD_CURRENT_STUDENTS(1),
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const result = response.data;
      setResponseMessage(result.message || 'Imported successfully.');
      setImportedRecords(result.insertedCount || rows.length);
      setUploadStatus('success');
      
      // Refresh uploaded students list if on details tab
      if (currentTab === 1) {
        fetchUploadedStudents();
        fetchStats();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorData = error.response?.data;
      
      // Handle duplicate error response
      if (errorData?.duplicates && errorData.duplicates.length > 0) {
        setErrorMessage(
          `Found ${errorData.duplicateCount} duplicate record(s) in the file. Please remove duplicates before importing.`
        );
        setDuplicates(errorData.duplicates);
      } else {
        setErrorMessage(errorData?.message || 'Failed to upload the file. Please try again.');
      }
      setUploadStatus('error');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} fullScreen>
      {isUploading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 3 }} />}

      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <Tooltip title="Close">
          <IconButton onClick={handleCloseDialog} color="error">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogTitle sx={adminStyles?.dialogTitle || { pr: 8, fontWeight: 'bold', color: 'primary.main' }}>
        Upload Current Students
      </DialogTitle>

      <DialogContent sx={adminStyles?.container || { bgcolor: '#f8f9fa', minHeight: '100vh', pt: 2 }}>
        {/* Tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
            <Tab 
              icon={<CloudUploadIcon />} 
              iconPosition="start"
              label="Upload" 
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<ListIcon />} 
              iconPosition="start"
              label="Uploaded Details" 
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        <MDBox display="flex" flexDirection="column" gap={2}>
          {/* Upload Tab Content */}
          {currentTab === 0 && (
            <>
          {/* SUCCESS */}
          {uploadStatus === 'success' && (
            <Card sx={{ p: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 1 }} />
                <MDTypography variant="h5" gutterBottom>
                  Upload Successful
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {responseMessage || 'Uploaded successfully.'}
                </MDTypography>
                <Chip label={`Records imported: ${importedRecords}`} color="success" variant="outlined" sx={{ mb: 2 }} />
                <Box>
                  <MDButton color="primary" onClick={resetAll}>
                    Upload Another File
                  </MDButton>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* ERROR */}
          {uploadStatus === 'error' && (
            <Card sx={{ p: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <ErrorIcon color="error" sx={{ fontSize: 64, mb: 1 }} />
                <MDTypography variant="h5" gutterBottom>
                  Upload Failed
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {errorMessage || 'There was an error uploading the file.'}
                </MDTypography>
                <Box>
                  <MDButton color="primary" onClick={resetAll}>
                    Try Again
                  </MDButton>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* MAIN CONTENT */}
          {!uploadStatus && (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <MDTypography variant="h6" fontWeight="medium">
                  Current Students Data Upload
                </MDTypography>

                <Box display="flex" gap={1} alignItems="center">
                  <MDButton
                    variant="outlined"
                    color="info"
                    startIcon={<DownloadIcon />}
                    onClick={downloadTemplate}
                    disabled={isDownloading}
                  >
                    {isDownloading ? 'Downloading…' : 'Download Template'}
                  </MDButton>

                  <MDButton
                    variant="outlined"
                    color="primary"
                    startIcon={<ListIcon />}
                    onClick={handleOpenUploadedDataDialog}
                  >
                    View Uploaded Data
                  </MDButton>
                </Box>
              </Box>

              <Alert icon={<InfoOutlinedIcon />} severity="info">
                Download the template, fill in the current student details, upload the Excel file, review the summary, and then confirm to import.
              </Alert>

              {/* Upload Card */}
              <Card
                variant="outlined"
                sx={{
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: file ? 'success.main' : 'divider',
                  bgcolor: '#fff',
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 5 }}>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="excel-file-input-current-students"
                  />
                  <UploadFileIcon sx={{ fontSize: 54, mb: 1, color: 'primary.main' }} />
                  <MDTypography variant="h6" gutterBottom>
                    {file ? file.name : 'Upload Excel File'}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {file
                      ? `${humanSize(file.size)} • Last modified: ${new Date(file.lastModified).toLocaleDateString()}`
                      : 'Drag & drop an Excel file here, or click to browse'}
                  </MDTypography>
                  <label htmlFor="excel-file-input-current-students">
                    <MDButton
                      variant="contained"
                      component="span"
                      color="info"
                      startIcon={<UploadFileIcon />}
                      disabled={parsing}
                    >
                      {file ? 'Change File' : 'Select File'}
                    </MDButton>
                  </label>
                  {parsing && (
                    <Box mt={2}>
                      <LoadingSkeleton variant="card" rows={1} showHeader={false} />
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Summary and Preview */}
              {!!rows.length && (
                <>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <MDTypography variant="subtitle1" fontWeight="medium">
                          Summary
                        </MDTypography>
                        <MDButton
                          size="small"
                          variant="outlined"
                          onClick={() => setShowPreview(!showPreview)}
                        >
                          {showPreview ? 'Hide Preview' : 'Show Preview'}
                        </MDButton>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                        <Chip label={`Sheet: ${sheetName || 'CurrentStudentsTemplate'}`} variant="outlined" />
                        <Chip label={`Columns: ${headers.length}`} variant="outlined" />
                        <Chip label={`Detected rows: ${rows.length}`} color="primary" />
                        {duplicates.length > 0 && (
                          <Chip
                            label={`Duplicates: ${duplicates.length}`}
                            color="error"
                            icon={<ErrorIcon />}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Duplicate Warning */}
                  {duplicates.length > 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <MDTypography variant="body2" fontWeight="medium" gutterBottom>
                        Found {duplicates.length} duplicate record(s) in the file:
                      </MDTypography>
                      <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                        {duplicates.slice(0, 5).map((dup, idx) => (
                          <li key={idx}>
                            <MDTypography variant="caption">
                              Row {dup.rowIndex}: {dup.name} - {dup.reason}
                            </MDTypography>
                          </li>
                        ))}
                        {duplicates.length > 5 && (
                          <li>
                            <MDTypography variant="caption">
                              ... and {duplicates.length - 5} more
                            </MDTypography>
                          </li>
                        )}
                      </Box>
                    </Alert>
                  )}

                  {/* Preview Table */}
                  {showPreview && (
                    <Card variant="outlined" sx={{ mt: 2 }}>
                      <CardContent>
                        <MDTypography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
                          Data Preview (First 10 rows)
                        </MDTypography>
                        <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>
                                  #
                                </TableCell>
                                {headers.slice(0, 8).map((header) => (
                                  <TableCell
                                    key={header}
                                    sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}
                                  >
                                    {header}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {rows.slice(0, 10).map((row, idx) => {
                                const isDuplicate = duplicates.some((d) => d.rowIndex === idx + 1);
                                return (
                                  <TableRow
                                    key={idx}
                                    sx={{
                                      bgcolor: isDuplicate ? 'error.lighter' : 'transparent',
                                    }}
                                  >
                                    <TableCell>{idx + 1}</TableCell>
                                    {headers.slice(0, 8).map((header) => (
                                      <TableCell key={header}>
                                        <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>
                                          {row[header] || '-'}
                                        </Typography>
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {rows.length > 10 && (
                          <MDTypography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Showing first 10 of {rows.length} rows
                          </MDTypography>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {errorMessage && (
                <Alert severity="error">
                  {errorMessage}
                </Alert>
              )}

              <Box display="flex" gap={2} mt="auto" justifyContent="flex-end">
                <MDButton variant="outlined" color="secondary" onClick={handleCloseDialog}>
                  Cancel
                </MDButton>
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={handleAskConfirm}
                  disabled={!file || parsing || rows.length === 0 || isUploading || duplicates.length > 0}
                  startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {isUploading ? 'Uploading…' : duplicates.length > 0 ? 'Fix Duplicates First' : 'Confirm & Upload'}
                </MDButton>
              </Box>
            </>
          )}
          </>
          )}

          {/* Uploaded Details Tab Content */}
          {currentTab === 1 && (
            <>
              {/* Statistics Cards */}
              {stats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {/* Total Students */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'primary.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PeopleIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.totalStudents || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Total Students
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Invitations Sent */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'success.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <SendIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.invitationSentCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Invitations Sent
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Invitations Pending */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'warning.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PendingIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.invitationPendingCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Invitations Pending
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Portal Connected */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'info.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <LinkIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.portalConnectedCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Portal Connected
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Requests Pending */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'warning.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <AssignmentIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.portalRequestPendingCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Requests Pending
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Requests Approved */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'success.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CheckCircleOutlineIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.portalRequestApprovedCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Requests Approved
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Requests Rejected */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: 'error.main',
                              borderRadius: '12px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CancelOutlinedIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <MDTypography variant="h5" fontWeight="bold" color="text">
                              {stats.portalRequestRejectedCount || 0}
                            </MDTypography>
                            <MDTypography variant="body2" color="text.secondary">
                              Requests Rejected
                            </MDTypography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Action Bar */}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="medium">
                  Uploaded Students ({uploadedStudents.length})
                </MDTypography>
                <Box display="flex" gap={1}>
                  <MDButton
                    variant="outlined"
                    color="primary"
                    startIcon={<ListIcon />}
                    onClick={handleOpenUploadedDataDialog}
                  >
                    View Details
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="info"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      fetchUploadedStudents();
                      fetchStats();
                    }}
                    disabled={loadingStudents}
                  >
                    Refresh
                  </MDButton>
                </Box>
              </Box>

              {/* Students Grid */}
              {loadingStudents ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : uploadedStudents.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <MDTypography variant="h6" color="text.secondary">
                      No uploaded students found
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Upload students using the Upload tab to see them here.
                    </MDTypography>
                  </CardContent>
                </Card>
              ) : (
                <Paper sx={{ height: 'calc(100vh - 400px)', width: '100%', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: '100%',
                      '& .ag-theme-alpine': {
                        fontSize: '0.75rem',
                        height: '100%',
                        width: '100%',
                        '& .ag-header-cell, & .ag-header-group-cell': {
                          fontSize: '0.75rem !important',
                          fontWeight: 600,
                          padding: '4px 8px !important',
                        },
                        '& .ag-cell': {
                          fontSize: '0.75rem !important',
                          padding: '4px 8px !important',
                          lineHeight: 1.4,
                        },
                        '& .ag-row': {
                          minHeight: '28px !important',
                        },
                        '& .ag-header-row': {
                          minHeight: '32px !important',
                        },
                        '& .ag-header-row-filter': {
                          minHeight: '22px !important',
                        },
                        '& .ag-floating-filter-input, & .ag-floating-filter-body': {
                          fontSize: '0.65rem !important',
                          height: '20px !important',
                          minHeight: '20px !important',
                        },
                        '& .ag-floating-filter-input input': {
                          fontSize: '0.65rem !important',
                          padding: '1px 3px !important',
                          height: '20px !important',
                          lineHeight: '20px !important',
                        },
                        '& .ag-floating-filter-wrapper': {
                          height: '20px !important',
                          minHeight: '20px !important',
                        },
                        '& .ag-header-cell-filtered': {
                          backgroundColor: '#e3f2fd !important',
                        },
                        '& .ag-paging-panel': {
                          fontSize: '0.75rem !important',
                          padding: '4px 8px !important',
                        },
                      },
                    }}
                  >
                    <AgGridReact
                      ref={gridRef}
                      rowData={uploadedStudents}
                      columnDefs={columnDefs}
                      defaultColDef={{
                        sortable: true,
                        resizable: true,
                        filter: true,
                        floatingFilter: true,
                        cellStyle: { fontSize: "0.75rem" },
                        filterParams: {
                          buttons: ['reset', 'apply'],
                          closeOnApply: true,
                        },
                      }}
                      pagination={true}
                      paginationPageSize={30}
                      animateRows={true}
                      rowHeight={28}
                      headerHeight={32}
                      floatingFiltersHeight={22}
                      className="ag-theme-alpine"
                      domLayout="normal"
                      suppressSizeToFit={true}
                      suppressFieldDotNotation={true}
                    />
                  </Box>
                </Paper>
              )}

              {errorMessage && (
                <Alert severity="error" onClose={() => setErrorMessage('')}>
                  {errorMessage}
                </Alert>
              )}
            </>
          )}
        </MDBox>
      </DialogContent>

      {/* Confirmation Dialog */}
      <MuiDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Upload</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text.secondary">
            Do you want to upload these current students? Detected rows: {rows.length}. Please ensure the columns match your template.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setConfirmOpen(false)}>
            No
          </MDButton>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleImport}
            autoFocus
            startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : null}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading…' : 'Yes, Upload'}
          </MDButton>
        </DialogActions>
      </MuiDialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Uploaded Current Students Data Dialog */}
      <AdminUploadedCurrentStudentsDialog
        open={uploadedDataDialogOpen}
        onClose={handleCloseUploadedDataDialog}
      />
    </Dialog>
  );
};

AdminUploadCurrentStudents.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminUploadCurrentStudents;
